import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Group,
  PageHeader,
  Stack,
  Text,
  Title,
} from '@vipso/shared';
import {
  useGetOrgDetailsQuery,
  useGetTaskInstancesQuery,
} from '../api/adminApi';
import { OrgDetailsPanel } from './OrgDetailsPanel';

export function OrgDetailsPage() {
  const { orgId = '' } = useParams<{ orgId: string }>();
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

  const { data, isLoading, isError } = useGetOrgDetailsQuery(
    { orgId },
    { skip: !orgId },
  );
  const { data: taskData, isLoading: tasksLoading } = useGetTaskInstancesQuery(
    { orgId, category: 'ONBOARDING' },
    { skip: !orgId },
  );

  const details = data?.orgDetails;
  const selectedRole = details?.roles.find((r) => r.id === selectedRoleId) ?? null;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <PageHeader
          title={details?.org.name ?? 'Organization'}
          description={
            details
              ? `${details.org.status} · Tenant ${details.org.tenantId} · ${details.org.uniqueId || '—'}`
              : 'Organization details'
          }
        />
        <Button component={Link} to="/orgs" variant="default">
          Back to organizations
        </Button>
      </Group>

      {isLoading ? (
        <Text c="dimmed">Loading...</Text>
      ) : isError || !details ? (
        <Alert color="red" title="Failed to load">
          Could not load organization details.
        </Alert>
      ) : (
        <OrgDetailsPanel
          details={details}
          tasksSection={
            <Stack gap="xs">
              <Title order={5}>Onboarding tasks</Title>
              {tasksLoading ? (
                <Text c="dimmed" size="sm">
                  Loading tasks...
                </Text>
              ) : (taskData?.taskInstances ?? []).length === 0 ? (
                <Text size="sm" c="dimmed">
                  No onboarding tasks for this organization.
                </Text>
              ) : (
                <Stack gap="xs">
                  {(taskData?.taskInstances ?? []).map((task) => (
                    <Group key={task.id} justify="space-between">
                      <Stack gap={2}>
                        <Text fw={600} size="sm">
                          {task.template?.title ?? task.templateId}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {task.template?.code} · {task.assigneeType}
                        </Text>
                      </Stack>
                      <Badge color={task.status === 'COMPLETED' ? 'green' : 'orange'}>
                        {task.status}
                      </Badge>
                    </Group>
                  ))}
                </Stack>
              )}
            </Stack>
          }
          rolesSection={
            <Stack gap="xs">
              <Title order={5}>Roles</Title>
              {details.roles.length === 0 ? (
                <Alert color="gray" title="No roles">
                  This organization has no roles yet.
                </Alert>
              ) : (
                <Stack gap="xs">
                  {details.roles.map((role) => (
                    <Group key={role.id} justify="space-between">
                      <Text fw={600}>{role.name}</Text>
                      <Button
                        size="xs"
                        variant="default"
                        onClick={() => setSelectedRoleId(role.id)}
                      >
                        View access
                      </Button>
                    </Group>
                  ))}
                </Stack>
              )}
              {selectedRole ? (
                <Stack gap="xs">
                  <Title order={5}>Access — {selectedRole.name}</Title>
                  <Group gap="xs">
                    {(selectedRole.featureKeys ?? []).length === 0 ? (
                      <Text size="sm" c="dimmed">
                        No feature keys
                      </Text>
                    ) : (
                      (selectedRole.featureKeys ?? []).map((key) => (
                        <Badge key={key} variant="light">
                          {key}
                        </Badge>
                      ))
                    )}
                  </Group>
                </Stack>
              ) : null}
            </Stack>
          }
        />
      )}
    </Stack>
  );
}
